export const CONSENT_KEY = 'odaro-analytics-consent';
export const CONSENT_VERSION = 1;
export const CONSENT_LIFETIME = 180 * 24 * 60 * 60 * 1000;
export const MEASUREMENT_ID = 'G-1ZQ4X7CKKS';
export type Consent = { version: number; analytics: boolean; savedAt: number; expiresAt: number };
export type AnalyticsEvent = 'demo_open' | 'quote_start' | 'contact_submit' | 'quote_submit';
const events = new Set<string>(['demo_open', 'quote_start', 'contact_submit', 'quote_submit']);

export function isAnalyticsHost(protocol: string, hostname: string) {
  return protocol === 'https:' && ['www.asystentremontowy.pl', 'asystentremontowy.pl'].includes(hostname);
}

export function analyticsCookieDeletions(cookieHeader: string, hostname: string, secure: boolean) {
  const names = cookieHeader.split(';').map(part => part.trim().split('=')[0]).filter(name => /^_ga(?:_[A-Za-z0-9_]+)?$/.test(name));
  const parts = hostname.split('.');
  const domains = [undefined, ...parts.map((_, index) => parts.slice(index).join('.')).filter(domain => domain.includes('.'))];
  return names.flatMap(name => domains.map(domain => `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${secure ? '; Secure' : ''}`));
}

export function makeConsent(analytics: boolean, now = Date.now()): Consent {
  return { version: CONSENT_VERSION, analytics, savedAt: now, expiresAt: now + CONSENT_LIFETIME };
}

export function parseConsent(raw: string | null, now = Date.now()): Consent | null {
  try {
    const value = JSON.parse(raw ?? 'null') as Partial<Consent> | null;
    if (!value || value.version !== CONSENT_VERSION || typeof value.analytics !== 'boolean' ||
      !Number.isFinite(value.savedAt) || !Number.isFinite(value.expiresAt) ||
      value.savedAt! > now || value.expiresAt! <= now || value.expiresAt! - value.savedAt! !== CONSENT_LIFETIME) return null;
    return value as Consent;
  } catch { return null; }
}

export function analyticsPage(pathname: string) {
  const path = pathname.replace(/\/$/, '') || '/';
  const titles: Record<string, string> = { '/': 'Odaro Studio', '/flowmate': 'FlowMate', '/privacy': 'Privacy policy', '/terms': 'Terms of use' };
  const safePath = Object.prototype.hasOwnProperty.call(titles, path) ? path : '/';
  return { page_location: `https://www.asystentremontowy.pl${safePath}`, page_title: titles[safePath], page_referrer: '' };
}

type Driver = {
  now(): number;
  allowed(): boolean;
  page(): ReturnType<typeof analyticsPage>;
  command(...args: unknown[]): void;
  load(): void;
  stop(): void;
  disable(value: boolean): void;
  clearCookies(): void;
};

export function createAnalytics(driver: Driver) {
  let choice: Consent | null = null;
  let initialized = false;
  const permitted = () => choice?.analytics === true && parseConsent(JSON.stringify(choice), driver.now()) !== null && driver.allowed();
  function stop() {
    driver.disable(true);
    if (initialized) driver.stop();
    initialized = false;
    driver.clearCookies();
  }
  function synchronize(next: Consent | null) {
    choice = next;
    if (!permitted()) { stop(); return; }
    if (initialized) return;
    initialized = true;
    driver.disable(false);
    driver.command('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    driver.command('js', new Date(driver.now()));
    driver.command('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    driver.command('config', MEASUREMENT_ID, {
      ...driver.page(), send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false, ignore_referrer: true,
      cookie_expires: Math.floor((choice!.expiresAt - driver.now()) / 1000),
      cookie_update: false, cookie_path: '/', cookie_flags: 'SameSite=Lax;Secure',
    });
    driver.command('event', 'page_view', { ...driver.page(), send_to: MEASUREMENT_ID });
    driver.load();
  }
  function track(event: AnalyticsEvent) {
    if (!events.has(event)) return false;
    if (!permitted()) { if (initialized) stop(); return false; }
    if (!initialized) return false;
    driver.command('event', event, { ...driver.page(), send_to: MEASUREMENT_ID });
    return true;
  }
  return { synchronize, track };
}
